/**
 * Wire-shape registry for the `provision-team` worker's step progression.
 *
 * One closed {@link StepLabel} union, one ordered {@link STEP_LABELS}
 * tuple, one {@link stepIndex} helper. Consumed by:
 *
 *   - the worker's `markStepStarted` + `markFailed` writers
 *     (`marketplace-api/src/workers/provision-team.ts`) — write side.
 *   - the Zod `pluginSourceAdminSchema` in
 *     `packages/marketplace-contracts/src/schemas/source.ts` — validates
 *     the wire shape on the way out of the API.
 *   - the DB CHECK constraint `plugin_sources_provision_step_chk` — same
 *     closed set, mirrored at the schema layer.
 *   - the UI's `deriveStepStatus` in
 *     `marketplace-ui/src/components/admin/ProvisionTeamDialog.tsx` —
 *     consumes `stepIndex(provisionStep)` to map the current label to a
 *     0-indexed position in the 9-pip timeline.
 *
 * No regex parsing, no string template — `plugin_sources.provision_step`
 * is a real column, written by the worker at every boundary.
 *
 * NOTE: this module has no internal imports — it stays in the contracts
 * package's wire surface and is safe for any workspace to pull. It does
 * NOT pull in the worker's heavyweight import tree (bullmq, ado-client,
 * etc.) — those live in `marketplace-api/src/workers/provision-team.ts`.
 */
/**
 * Ordered tuple of every {@link StepLabel}. The order matches the 9-step
 * ordered sequence the `provision-team` worker walks; this constant is a
 * label set, not a transition graph — illegal step transitions are not
 * representable, since the worker is the only writer and enforces the
 * order procedurally. Append-only; removing a label is a wire-shape break.
 *
 * The `satisfies readonly StepLabel[]` clause is load-bearing: adding to
 * the union without updating the tuple (or vice versa) fails typecheck.
 */
export const STEP_LABELS = [
    'load-source',
    'create-repo',
    'push-template',
    'patch-source',
    'set-policies',
    'register-hook',
    'register-pipeline',
    'finalize-team',
    'audit-provisioned',
];
/**
 * Canonical terminal step. The worker writes this label via
 * `markStepStarted` immediately before `emitProvisionedAudit`; once the
 * `source.provisioned` audit row commits, the UI's success branch flips
 * every pip to 'done'. Exported as a named constant so consumers don't
 * couple to a positional convention (`STEP_LABELS[STEP_LABELS.length-1]`).
 */
export const TERMINAL_STEP = 'audit-provisioned';
/**
 * Position of `label` in {@link STEP_LABELS} (0-indexed). Use this rather
 * than open-coding `STEP_LABELS.indexOf(...)` so monotonic-advance checks
 * stay aligned with the canonical order. Returns -1 when `label` is null
 * OR when `label` is not in the closed union (forward-compat: a future
 * worker emitting an off-union value before the consumer is redeployed
 * surfaces as -1 at the consumer boundary).
 */
export function stepIndex(label) {
    if (label === null)
        return -1;
    return STEP_LABELS.indexOf(label);
}
//# sourceMappingURL=provision-steps.js.map